import { alarm, CreateAlarmRequest } from "@/endpoints/dryp/alarm";
import { AsyncStateStore } from "@/utils/AsyncStateStore";
import { makeAutoObservable } from "mobx";
import { UseFormReturn } from "react-hook-form";
import { toast } from "sonner";
import { ModalStore } from "@/stores/ui/ModalStore";
export interface WizardFormData extends CreateAlarmRequest {
	id?: Dryp.AlarmId;
}

export const toCreatePayload = (data: WizardFormData) => {
	const hasWarning = data.warningThreshold != null;
	const usesDuration = (data.durationMinutes ?? 0) > 0;
	const usesDatapoints = !usesDuration && (data.datapointCount ?? 0) > 0;
	return {
		...data,
		recipients: data.recipients.map(recipient =>
			recipient.userId ? { userId: recipient.userId } : { name: recipient.name, email: recipient.email },
		),
		warningThreshold: hasWarning ? data.warningThreshold : null,
		warningDeliveryMode: hasWarning ? data.warningDeliveryMode : null,
		durationMinutes: usesDuration ? data.durationMinutes : null,
		datapointCount: usesDatapoints ? data.datapointCount : null,
	};
};
type AlarmStepType = "info" | "condition" | "scope" | "recipients";
type AlarmStepConfig = {
	type: AlarmStepType;
	label: string;
	fields: (keyof WizardFormData)[];
};

export const ALARM_STEPS_CONFIG: AlarmStepConfig[] = [
	{ type: "info", label: "Info", fields: ["alarmType", "name"] },
	{
		type: "condition",
		label: "Condition",
		fields: [
			"parameterId",
			"operator",
			"criticalThreshold",
			"warningThreshold",
			"durationMinutes",
			"datapointCount",
		],
	},
	{ type: "scope", label: "Where", fields: ["structureScope", "structureIds"] },
	{
		type: "recipients",
		label: "Notification",
		fields: ["criticalDeliveryMode", "warningDeliveryMode", "cooldownMinutes", "recipients"],
	},
];

export const stepsLength = ALARM_STEPS_CONFIG.length;
type WizardType = "create" | "update";
export class AlarmWizardStore {
	private step = 0;
	private type: WizardType = "create";
	public form: UseFormReturn<WizardFormData>;
	private create = new AsyncStateStore(alarm.create);
	private update = new AsyncStateStore(alarm.update);
	private fetchAlarm = new AsyncStateStore(alarm.getById);
	private modalStore: ModalStore;
	private onSuccess?: () => void;

	constructor(
		form: UseFormReturn<WizardFormData>,
		modalStore: ModalStore,
		alarmId?: Dryp.AlarmId,
		onSuccess?: () => void,
	) {
		this.form = form;
		this.modalStore = modalStore;
		this.onSuccess = onSuccess;
		makeAutoObservable(this, { form: false });
		if (alarmId) {
			this.type = "update";
			this.loadForEdit(alarmId);
		}
	}

	private getStepConfig(step: number) {
		return ALARM_STEPS_CONFIG?.[step];
	}

	private async loadForEdit(alarmId: Dryp.AlarmId) {
		const details = await this.fetchAlarm.fetch(alarmId);
		if (!details) return;
		this.form.reset({
			id: details.id,
			name: details.name,
			alarmType: details.alarmType,
			parameterId: details.parameter.id,
			operator: details.operator,
			criticalThreshold: details.criticalThreshold,
			criticalDeliveryMode: details.criticalDeliveryMode,
			warningThreshold: details.warningThreshold,
			warningDeliveryMode: details.warningDeliveryMode,
			durationMinutes: details.durationMinutes,
			datapointCount: details.datapointCount,
			structureScope: details.structureScope,
			structureIds: details.structureIds,
			cooldownMinutes: details.cooldownMinutes,
			recipients: details.recipients,
		});
	}

	get isLastStep() {
		return this.step === stepsLength - 1;
	}
	get currentStep() {
		return this.step;
	}

	get wizarType() {
		return this.type;
	}

	get isLoading() {
		return this.create.loading || this.update.loading || this.fetchAlarm.loading;
	}

	get isFetchingAlarm() {
		return this.fetchAlarm.loading;
	}

	get fetchAlarmError() {
		const apiError = this.fetchAlarm.error as ApiError | null;
		if (!apiError) return null;
		return apiError.message || "Failed to load alarm. Please close and try again.";
	}

	get submitError(): ApiError | null {
		return (this.update.error || this.create.error) as ApiError | null;
	}

	private async submit() {
		const ok = await this.form.trigger();
		if (!ok) return;
		const data = this.form.getValues();
		const payload = toCreatePayload(data);
		const isUpdate = this.type === "update" && data.id;
		try {
			if (isUpdate) {
				await this.update.fetch(data.id as Dryp.AlarmId, payload);
			} else {
				await this.create.fetch(payload);
			}
			toast.success(isUpdate ? "Alarm updated successfully" : "Alarm created successfully");
			this.onSuccess?.();
			this.modalStore.close();
		} catch (error) {
			const apiError = error as ApiError;
			toast.error(apiError?.message || `Failed to ${isUpdate ? "update" : "create"} alarm. Please try again.`);
		}
	}

	async goNext() {
		if (this.isLastStep) {
			await this.submit();
			return;
		}
		const currentConfig = this.getStepConfig(this.currentStep);
		const ok = await this.form.trigger(currentConfig.fields);
		if (ok) {
			this.incrementStep();
		}
	}

	private incrementStep() {
		this.step = this.step + 1;
	}

	goBack() {
		if (this.step === 0) {
			return;
		}
		this.step = this.step - 1;
	}
}
